import { useVbsWorkOrderShowQuery } from "@/services/backend/vbs/work-orders/api/work-order-api";
import {
  UpdateItemRequest,
  useVbsDocumentationDeleteImageMutation,
  useVbsDocumentationShowImageQuery,
  useVbsDocumentationShowQuery,
  useVbsDocumentationUploadImageMutation,
  useVbsDocumentUpdateItemMutation,
} from "@/services/backend/vbs/work-orders/api/documentation-api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Edit, RefreshCw, Trash } from "lucide-react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Image,
  Item,
} from "@/services/backend/vbs/work-orders/types/documentation";
import { WorkOrder } from "@/services/backend/vbs/work-orders/types/work-order";
import { useEffect, useState } from "react";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { useVbsTreatmentShowQuery } from "@/services/backend/vbs/treatments/api/treatment-api";
import {
  useErrValidationFailedContext,
  ErrValidationFailedContextProvider,
} from "@/shared/lib/errors/err-validation-failed-context";
import { Label } from "@/shared/components/ui/label";
import t from "@/lang/lang";
import { Input } from "@/shared/components/ui/input";
import { InputValidationErrors } from "@/shared/components/errors/input-error-messages";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Textarea } from "@/shared/components/ui/textarea";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { cn } from "@/shared/lib/utils";
import imageCompression from "browser-image-compression";
import { v4 } from "uuid";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Card } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";

export function EditItemDialog({
  itemId,
  documentationId,
  workOrderId,
  disabled = false,
}: {
  documentationId: string;
  itemId: string;
  workOrderId: string;
  disabled?: boolean;
}) {
  const { data: workOrder } = useVbsWorkOrderShowQuery({ id: workOrderId! });
  const { data: documentation } = useVbsDocumentationShowQuery({
    id: documentationId!,
  });
  // The work order's readiness check reports an insufficient documentation.
  // Assigning a position and adding or deleting images may fix it.
  const { resetField } = useErrValidationFailedContext();

  const item = documentation!.items.find((i) => i.id === itemId);
  if (!item) {
    return <RTKQueryErrorAlert error={{ status: 404 }} />;
  }

  // This is impossible because the dialog is only rendered
  // when the item exists.
  if (!item) return null;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Edit className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-10/12 max-w-10/12 overflow-y-auto sm:max-w-10/12">
        <DialogHeader>
          <DialogTitle>{item.title}</DialogTitle>
          <DialogDescription>
            {t("Details zur Dokumentation")}
          </DialogDescription>
        </DialogHeader>
        {/* The item belongs to the documentation, not to the work order. */}
        <ErrValidationFailedContextProvider>
          <ItemContent
            item={item}
            workOrder={workOrder!}
            disabled={disabled}
            onDocumentationChange={() => resetField("documentation")}
          />
        </ErrValidationFailedContextProvider>
      </DialogContent>
    </Dialog>
  );
}

function ItemContent({
  item,
  workOrder,
  disabled,
  onDocumentationChange,
}: {
  item: Item;
  workOrder: WorkOrder;
  disabled: boolean;
  onDocumentationChange: () => void;
}) {
  const [request, setRequest] = useState<UpdateItemRequest>({
    id: workOrder.documentationId,
    itemId: item.id,
    positionId: item.positionId ?? "",
    title: item.title,
    description: item.description,
    isInternal: item.isInternal,
  });
  const mutation = useVbsDocumentUpdateItemMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    id: workOrder.documentationId,
    itemId: item.id,
    positionId: item.positionId ?? "",
    title: item.title,
    description: item.description,
    isInternal: item.isInternal,
  });
  const { data: treatment } = useVbsTreatmentShowQuery({
    id: workOrder.treatmentId,
  });

  return (
    <div className="space-y-4">
      <div className="items-start md:flex md:space-x-8">
        <div className="grid w-32 items-center gap-1.5">
          <Label htmlFor="number">{t("Nr.")}</Label>
          <Input
            disabled
            readOnly
            type="number"
            id="number"
            placeholder="Nummer"
            value={item.number}
          />
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="title">{t("Titel")}</Label>
          <Input
            type="text"
            id="title"
            placeholder="Titel"
            value={request.title}
            onChange={(e) => setRequest({ ...request, title: e.target.value })}
            disabled={disabled}
          />
          <InputValidationErrors error={error} field="title" />
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="requirementId">
            {t("Arbeit Zuordnen (Optional)")}
          </Label>
          <Select
            value={request.positionId ?? ""}
            onValueChange={(newVal) => {
              onDocumentationChange();
              setRequest({ ...request, positionId: newVal });
            }}
            disabled={disabled}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("Position auswählen")} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>{t("Positionen")}</SelectLabel>
                {treatment?.positions.map((position) => (
                  <SelectItem value={position.id} key={position.id}>
                    <span>{position.number}</span>
                    <span> - </span>
                    <span>{t(position.component.kind)}</span>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <InputValidationErrors error={error} field="positionId" />
        </div>
      </div>
      <div className="grid w-full gap-1.5">
        <Label htmlFor="description">{t("Beschreibung")}</Label>
        <Textarea
          placeholder={t("Beschreibung der Verschmutzung")}
          id="description"
          value={request.description}
          onChange={(e) =>
            setRequest({ ...request, description: e.target.value })
          }
          disabled={disabled}
        />
        <InputValidationErrors error={error} field="description" />
      </div>
      <div className="grid w-full gap-1.5">
        <Label>{t("Interne Dokumentation")}</Label>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="terms1"
            checked={request.isInternal}
            onCheckedChange={(newVal) => {
              setRequest({ ...request, isInternal: newVal as boolean });
            }}
            disabled={disabled}
          />
          <p className="text-muted-foreground text-sm">
            {t("Dem Kunden diese Dokumentation nicht anzeigen.")}
          </p>
        </div>
      </div>
      <div className="w-full md:grid md:grid-cols-2 md:gap-8">
        <div className="space-y-2">
          <div className="text-lg font-medium">{t("Vorher")}</div>
          <ImageUpload
            documentationId={workOrder.documentationId}
            itemId={item.id}
            beforeOrAfter="before"
            onUpload={onDocumentationChange}
            disabled={disabled}
          />
          <div className="space-y-2">
            <Label>{t("Hochgeladene Bilder:")}</Label>
            <div className="w-full space-y-4">
              {item.beforeImages.map((image) => (
                <ShowImage
                  key={image.id}
                  documentationId={workOrder.documentationId}
                  itemId={item.id}
                  image={image}
                  onDelete={onDocumentationChange}
                  disabled={disabled}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="text-lg font-medium">{t("Nachher")}</div>
          <ImageUpload
            documentationId={workOrder.documentationId}
            itemId={item.id}
            beforeOrAfter="after"
            onUpload={onDocumentationChange}
            disabled={disabled}
          />
          <div className="space-y-2">
            <Label>{t("Hochgeladene Bilder:")}</Label>
            <div className="w-full space-y-4">
              {item.afterImages.map((image) => (
                <ShowImage
                  key={image.id}
                  documentationId={workOrder.documentationId}
                  itemId={item.id}
                  image={image}
                  onDelete={onDocumentationChange}
                  disabled={disabled}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImageUpload(props: {
  documentationId: string;
  itemId: string;
  beforeOrAfter: "before" | "after";
  onUpload: () => void;
  disabled?: boolean;
}) {
  const {
    beforeOrAfter,
    documentationId,
    itemId,
    onUpload,
    disabled = false,
  } = props;
  const [upload, { isLoading, error, isSuccess, reset }] =
    useVbsDocumentationUploadImageMutation();

  const { toast } = useToast();

  useEffect(() => {
    if (isSuccess) {
      toast({
        title: t("Bild hochgeladen"),
        description: t("Das Bild wurde erfolgreich hochgeladen."),
        variant: "success",
      });
      onUpload();
      reset();
    }
  }, [isSuccess, reset, toast, onUpload]);

  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
      reset();
    }
  }, [error, reset, toast]);

  return (
    <Label
      className={cn(
        "ring-offset-background focus-visible:ring-ring inline-flex w-full items-center justify-center rounded-md text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50",
        "border-input bg-background hover:bg-accent hover:text-accent-foreground border",
        "h-9 rounded-md px-3",
      )}
    >
      <Input
        type="file"
        className="hidden"
        accept="image/jpeg,image/png"
        onChange={async (e) => {
          if (e.target.files && e.target.files[0]) {
            // compress image
            const options = {
              maxSizeMB: 0.3,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            };
            try {
              const compressedFile = await imageCompression(
                e.target.files[0],
                options,
              );
              // upload compressed image
              upload({
                id: documentationId,
                itemId,
                beforeOrAfter,
                imageId: v4(),
                image: compressedFile,
              });
              e.target.value = "";
              e.target.files = null;
            } catch (err) {
              const parse = parseRTKQueryError(err as FetchBaseQueryError);
              toast({
                title: parse?.title,
                description: parse?.description,
                variant: "destructive",
              });
            }
          }
        }}
        disabled={isLoading || disabled}
      />
      {isLoading && <RefreshCw className="mr-2 h-4 w-4 animate-spin" />}
      <span>{t("Foto hinzufügen")}</span>
    </Label>
  );
}

function ShowImage({
  documentationId,
  itemId,
  image,
  onDelete,
  disabled = false,
}: {
  documentationId: string;
  itemId: string;
  image: Image;
  onDelete: () => void;
  disabled?: boolean;
}) {
  const {
    data: imageData,
    isLoading,
    error,
  } = useVbsDocumentationShowImageQuery({
    id: documentationId,
    imageId: image.id,
  });

  if (error) {
    return (
      <div className="relative">
        <RTKQueryErrorAlert error={error} />
        <DeleteImageButton
          documentationId={documentationId}
          itemId={itemId}
          imageId={image.id}
          onDelete={onDelete}
          disabled={disabled}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <Card className="text-muted-foreground animate-pulse p-6">
        {t("Lade Daten...")}
      </Card>
    );
  }

  return (
    <div className="relative">
      <img className="h-auto rounded-lg" src={imageData!.objectURL} alt="" />
      <DeleteImageButton
        documentationId={documentationId}
        itemId={itemId}
        imageId={image.id}
        onDelete={onDelete}
        disabled={disabled}
      />
    </div>
  );
}

function DeleteImageButton(props: {
  documentationId: string;
  itemId: string;
  imageId: string;
  onDelete: () => void;
  disabled?: boolean;
}) {
  const {
    documentationId,
    itemId,
    imageId,
    onDelete,
    disabled = false,
  } = props;
  const [open, setOpen] = useState(false);
  const [deleteImage, { isLoading, error, isSuccess, reset }] =
    useVbsDocumentationDeleteImageMutation();

  const onClick = () => {
    if (isLoading) {
      return;
    }
    deleteImage({
      id: documentationId,
      itemId,
      imageId,
    });
  };

  useEffect(() => {
    if (isSuccess) {
      onDelete();
      reset();
      setOpen(false);
    }
  }, [isSuccess, setOpen, reset, onDelete]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="absolute top-4 right-4" disabled={disabled}>
          <Trash className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>{t("Bild wirklich löschen?")}</DialogTitle>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <Button onClick={onClick} variant="destructive">
            {t("Löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
